import React, { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

export const NewsletterForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');
    setDetails('');

    try {
      // More detailed environment variable logging
      const rawApiKey = import.meta.env.VITE_MAILERLITE_API_KEY;
      console.log('Raw API key type:', typeof rawApiKey);
      console.log('Raw API key value:', rawApiKey);
      
      const apiKey = rawApiKey?.trim();
      const groupId = import.meta.env.VITE_MAILERLITE_GROUP_ID;
      
      console.log('API Key Details:', {
        rawLength: rawApiKey?.length,
        trimmedLength: apiKey?.length,
        isEqual: rawApiKey === apiKey,
        containsQuotes: apiKey?.includes('"') || apiKey?.includes("'"),
        containsNewlines: apiKey?.includes('\n'),
        containsSpaces: apiKey?.includes(' '),
        firstChar: apiKey?.[0],
        lastChar: apiKey?.[apiKey?.length - 1]
      });

      // First check if we have a valid email
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address');
      }

      if (!apiKey) {
        throw new Error('API key is not configured');
      }

      const authHeader = `Bearer ${apiKey}`;
      console.log('Authorization header:', {
        length: authHeader.length,
        firstChars: authHeader.substring(0, 30),
        lastChars: authHeader.substring(authHeader.length - 30)
      });

      const requestBody = {
        email,
        groups: [groupId],
        fields: {}
      };

      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': authHeader
      };

      const response = await fetch('https://connect.mailerlite.com/api/subscribers', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      const responseText = await response.text();
      console.log('Response details:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers),
        text: responseText,
        requestHeaders: headers
      });

      let data;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch (e) {
        console.error('Failed to parse response:', e);
        throw new Error(`Invalid server response: ${responseText}`);
      }

      if (!response.ok || !data) {
        const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
        const errorDetails = data?.errors ? JSON.stringify(data.errors, null, 2) : responseText;
        const error = new Error(errorMessage);
        (error as any).cause = errorDetails;
        throw error;
      }

      setStatus('success');
      setMessage('Thanks for subscribing! Please check your email to confirm.');
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        details: error instanceof Error ? (error as any).cause : undefined,
        stack: error instanceof Error ? error.stack : undefined
      });
      setStatus('error');
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to subscribe. Please try again.';
      setMessage(errorMessage);

      // Always show error details in development
      if (process.env.NODE_ENV === 'development') {
        if (error instanceof Error) {
          setDetails(
            [
              (error as any).cause || '',
              error.stack || '',
              'Full error: ' + JSON.stringify(error, null, 2)
            ].filter(Boolean).join('\n\n')
          );
        } else {
          setDetails(JSON.stringify(error, null, 2));
        }
      }
      // In production, only show details if we have specific error details
      else if (error instanceof Error && (error as any).cause) {
        setDetails((error as any).cause);
      }
    }
  };

  return (
    <div className="glass-card rounded-xl p-6 my-8">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Stay Updated
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Subscribe to get the latest chart updates and music trends delivered to your inbox.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <div className="relative">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent outline-none transition-all"
              required
              disabled={status === 'loading'}
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Subscribe'
              )}
            </button>
          </div>
          {message && (
            <div className={`mt-3 ${
              status === 'error' ? 'bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/50 rounded-lg p-3' : ''
            }`}>
              <p className={`text-sm flex items-start gap-2 ${
                status === 'success' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {status === 'error' && <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <span>{message}</span>
              </p>
              {details && (
                <pre className="mt-2 text-xs text-red-500 dark:text-red-400 overflow-auto">
                  {details}
                </pre>
              )}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default NewsletterForm; 
